import { Button } from "@components/button";
import { EditSimpleIcon } from "@components/icon";
import {
    ButtonGroupStyled,
    Cell,
    HeaderCell,
    PlaceholderWrapper,
    SectionTitle,
    StyledFormTableList,
    Wrapper
} from "@pages/payroll/payslip/formTableList/FormTableList.styles";
import { addFakeColumns, printItemValue } from "@pages/payroll/payslip/formTableList/FormTableList.utils";
import i18next from "i18next";
import React, { useCallback } from "react";

import { TextAlign } from "../../../../enums";
import TestIds from "../../../../testIds";

export type TFormTableListValue = number | string | null | undefined;

export interface IFormTableListPropData {
    editableInfo?: IEditableInfoDef;
    value: TFormTableListValue;
    formatter?: (value: number) => string | React.ReactElement;
}

export interface IFormTableListAdditionalColumn extends Omit<IFormTableListPropData, "editableInfo"> {
    id: string;
    align?: TextAlign;
}

export interface IEditableInfoDef {
    Id: number;
    path?: string; // empty for payslip Items, otherwise path to parent employment, e.g. "Employments(5)"
    propName: string; // name of the property to edit, including Override suffix
    origValue: TFormTableListValue; // original value of the property
    unit?: string;
}

export interface IFormTableListRow extends IFormTableListPropData {
    title: string;
    isSubRow?: boolean;
    isHeader?: boolean;
    isMarked?: boolean;
    isFullWidth?: boolean;
    additionalColumns?: IFormTableListAdditionalColumn[];
}

export interface IFormTableListSection {
    title?: string;
    rows: IFormTableListRow[];
}

interface IProps {
    sections: IFormTableListSection[];
    hasPinkBackground?: boolean;
    onEdit?: () => void;
    isEditable?: boolean;
    placeholder?: string;
    minWidth?: number;
    hasDynamicColumns?: boolean;
}

// component to render a table list in form view, possible sections, rows and sub-rows
// wrapper can have pink background
// local for now, we may move it to components in case it's reused somewhere else
const FormTableList: React.FC<IProps> = ({
                                             sections,
                                             hasPinkBackground,
                                             onEdit,
                                             placeholder,
                                             isEditable,
                                             minWidth,
                                             hasDynamicColumns
                                         }) => {

    const handleClick = useCallback(() => {
        onEdit();
    }, [onEdit]);

    if (!sections?.length || sections?.length <= 0) {
        return null;
    }

    // loop through sections and count max number of columns
    const baseColumnsCount = 2;
    let maxAdditionalColumns = 0;
    sections.forEach((section) => {
        section.rows.forEach((row) => {
            if (row.additionalColumns?.length > maxAdditionalColumns) {
                maxAdditionalColumns = row.additionalColumns.length;
            }
        });
    });
    const maxColumns = maxAdditionalColumns + baseColumnsCount;

    return (
        <StyledFormTableList $hasBackground={hasPinkBackground} data-testid={TestIds.FormTableList}>
                {placeholder ? (
                        <PlaceholderWrapper data-testid={TestIds.Text}>{placeholder}</PlaceholderWrapper>
                ) : (
                    <Wrapper $minWidth={minWidth} $columns={hasDynamicColumns ? maxColumns : null}>
                            {sections.map((section, index) => (
                                    <React.Fragment key={`section-${index}`}>
                                        {(section.title || hasPinkBackground) && (<SectionTitle $columns={maxColumns} data-testid={TestIds.SectionTitle}>
                                            {(section.title && <span>{section.title}</span>)}
                                        </SectionTitle>)}
                                        {section.rows.map((row, index) => {
                                            const rowKey = `row-${index}`;
                                            const cellProps = {
                                                $isSubRow: row.isSubRow,
                                                $isBold: row.isHeader,
                                            };
                                            let additionalColumns: React.ReactElement[] = null;
                                            if (row.additionalColumns) {
                                                const columns = addFakeColumns(row.additionalColumns, maxAdditionalColumns);
                                                additionalColumns = columns.map((col) => {
                                                    return (
                                                            <Cell key={`column-${col.id}`}
                                                                  $align={col.align} {...cellProps}>
                                                                {printItemValue(col)}
                                                            </Cell>
                                                    );
                                                });
                                            }
                                            const Component = row.isHeader ? HeaderCell : Cell;
                                            const previousIsHeaderCell = index > 0 ? section.rows[index - 1].isHeader : false;
                                            if (row.isFullWidth) {
                                                return (
                                                        <Component key={rowKey}
                                                                   $columns={maxColumns}
                                                                   $isSubRow={row.isSubRow}
                                                                   $woTopMargin={previousIsHeaderCell}
                                                                   $isBold={row.isHeader}
                                                                   $align={TextAlign.Left}
                                                                   $hasMark={row.isMarked}>{row.title}</Component>
                                                );
                                            }
                                            return (
                                                    <React.Fragment key={rowKey}>
                                                        <Component $isSubRow={row.isSubRow}
                                                                   $woTopMargin={previousIsHeaderCell}
                                                                   $isBold={row.isHeader}
                                                                   $align={TextAlign.Left}
                                                                   $hasMark={row.isMarked}>{row.title}</Component>
                                                        {additionalColumns ? additionalColumns : (maxAdditionalColumns ? (
                                                            <Cell $columns={maxAdditionalColumns} />) : null)}
                                                        <Cell {...cellProps} data-id={row.title} data-testid={TestIds.Text}>
                                                            {printItemValue(row)}
                                                        </Cell>
                                                    </React.Fragment>
                                            );
                                        })}
                                    </React.Fragment>
                            ))}
                        </Wrapper>
                )}
                {isEditable && onEdit && (
                        <ButtonGroupStyled align={TextAlign.Right}>
                            <Button
                                    icon={<EditSimpleIcon/>}
                                    isTransparent
                                    onClick={handleClick}>
                                {i18next.t("Common:General.Edit")}
                            </Button>
                        </ButtonGroupStyled>
                )}
            </StyledFormTableList>
    );
};

export default FormTableList;